import pluralize from 'pluralize';
import { memo, type ReactElement, useState } from 'react';

import { MinusIcon } from '@air/design-icons';
import { DropdownMenu } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { SelectionList } from '~/components/v2/components/shared/selectionBar/components/SelectionList';

export type SelectionItemsDropdownProps = {
  selectedItemCount: number;
  /**
   * Must be a single host element (`button` / `div`) that can take a ref — `DropdownMenu` uses
   * `asChild`. Prefer a `button` and `group-data-[state=open]:…` for open-state styling so the
   * trigger does not need to remount when the menu opens.
   */
  trigger: ReactElement;
};

/**
 * Shared selected-items list shell for both the default and agent selection bars.
 * Callers own trigger chrome; this owns the menu, sticky header, and `SelectionList`.
 *
 * Opens upward — selection bars sit at the bottom of the viewport.
 * Only mounted when the list is eligible (`!selectAll && !hasOnlyCanvasesSelected`), so the header
 * always reflects the explicit selection count.
 */
export const SelectionItemsDropdown = memo(({ selectedItemCount, trigger }: SelectionItemsDropdownProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

  return (
    <DropdownMenu
      className="max-h-[400px] w-[400px] overflow-y-auto p-0"
      trigger={trigger}
      size="large"
      side="top"
      sideOffset={12}
      open={isMenuOpen}
      onOpenChange={setIsMenuOpen}
      disabled={selectedItemCount <= 1}
      ref={setScrollElement}
    >
      <div className="flex select-none flex-col">
        <div className="sticky top-0 z-30 flex w-full items-center justify-between gap-2.5 ads:bg-base bg-gray-1 px-3 pt-3 pb-3">
          <div className="ml-1 font-medium ads:text-primary text-14 text-gray-12">
            {`${selectedItemCount} ${pluralize('item', selectedItemCount)} selected`}
          </div>
          <IconButton
            adsIcon
            icon={MinusIcon}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => setIsMenuOpen(false)}
            label="Close"
            appearance="ghost"
            color="gray"
            size="small"
          />
        </div>
        <SelectionList scrollElement={scrollElement} />
      </div>
    </DropdownMenu>
  );
});

SelectionItemsDropdown.displayName = 'SelectionItemsDropdown';
